/**
 * The app's screen flow as a tiny state machine (no router library, no URLs):
 *
 *   Start ──Play──▶ (starting) ──ticket──▶ Game ──game over──▶ GameOver
 *     │                                                        │
 *     ├─▶ Leaderboards / Settings / Profile ◀──────────────────┤
 *     │         └──▶ Onboarding (join / edit)                  │
 *     └──────────────────────── Home ◀─────────────────────────┘
 *
 * Menu screens form a stack, so "Back" returns wherever the player came from
 * (GameOver → Join → back to GameOver). A run replaces the whole stack.
 * Pure and framework-free so the transitions are unit-tested.
 */
import type { SessionTicket } from '../../contracts.ts';
import type { OnboardingStepId } from './onboarding.ts';

export type OnboardingMode = 'join' | 'edit';

export type Route =
  | { name: 'start' }
  | { name: 'game'; ticket: SessionTicket; run: number }
  | { name: 'gameOver' }
  | { name: 'settings' }
  | { name: 'leaderboards' }
  | { name: 'profile' }
  | { name: 'onboarding'; mode: OnboardingMode; step: OnboardingStepId };

/** Screens that can be opened from a menu (pushed on the stack). */
export type MenuRoute = Extract<Route, { name: 'settings' | 'leaderboards' | 'profile' | 'onboarding' }>;

export interface RouterState {
  /** Never empty; the last entry is on screen. */
  stack: readonly Route[];
  /** Play was pressed and the session ticket is on its way (the Play button shows a spinner). */
  starting: boolean;
  /** Increments per run; used as the GameScreen key so every run mounts fresh. */
  runs: number;
}

export type RouterAction =
  | { type: 'play' }
  | { type: 'playReady'; ticket: SessionTicket }
  | { type: 'playCancelled' }
  | { type: 'gameOver' }
  | { type: 'open'; route: MenuRoute }
  | { type: 'back' }
  | { type: 'home' };

export const INITIAL_ROUTER: RouterState = { stack: [{ name: 'start' }], starting: false, runs: 0 };

export function currentRoute(state: RouterState): Route {
  return state.stack[state.stack.length - 1] ?? { name: 'start' };
}

/** Where Play can be pressed. */
export function canPlayFrom(route: Route): boolean {
  return route.name === 'start' || route.name === 'gameOver';
}

export function routerReducer(state: RouterState, action: RouterAction): RouterState {
  const top = currentRoute(state);
  switch (action.type) {
    case 'play':
      return canPlayFrom(top) && !state.starting ? { ...state, starting: true } : state;

    case 'playReady': {
      if (!state.starting) return state; // the player left before the ticket arrived
      const runs = state.runs + 1;
      return { stack: [{ name: 'game', ticket: action.ticket, run: runs }], starting: false, runs };
    }

    case 'playCancelled':
      return state.starting ? { ...state, starting: false } : state;

    case 'gameOver':
      return top.name === 'game' ? { ...state, stack: [{ name: 'gameOver' }] } : state;

    case 'open': {
      if (top.name === 'game' || state.starting) return state;
      // Re-opening the screen that is already showing replaces it (e.g. onboarding at another step).
      const base = top.name === action.route.name ? state.stack.slice(0, -1) : state.stack;
      return { ...state, stack: [...base, action.route] };
    }

    case 'back': {
      if (top.name === 'game' || state.starting) return state;
      if (state.stack.length > 1) return { ...state, stack: state.stack.slice(0, -1) };
      return top.name === 'start' ? state : { ...state, stack: [{ name: 'start' }] };
    }

    case 'home':
      if (top.name === 'game') return state;
      return { ...state, stack: [{ name: 'start' }], starting: false };
  }
}
